import { createApi } from '@reduxjs/toolkit/query/react'
import { createBaseQueryWithReauth } from 'store/auth/base-query-with-reauth'
import {
  CheckoutSessionStatusResponse,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreateSetupIntentRequest,
  CreateSetupIntentResponse,
  SubscriptionInvoice,
  SubscriptionLookup,
  UpdatePaymentMethodRequest,
  UpdatePaymentMethodResponse,
} from './subscriptions.types'

const baseUrl = `${process.env.REACT_APP_ONBOARDING_SERVICE_API}`

export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: createBaseQueryWithReauth(baseUrl!),
  tagTypes: ['Subscription', 'SubscriptionInvoice'],
  endpoints: (builder) => ({
    getSubscriptionByUserId: builder.query<SubscriptionLookup, { userId: string }>(
      {
        query: ({ userId }) => ({
          url: `oapi/v1/subscriptions?userId=${encodeURIComponent(userId)}`,
        }),
        providesTags: ['Subscription'],
      }
    ),

    createCheckoutSession: builder.mutation<
      CreateCheckoutSessionResponse,
      CreateCheckoutSessionRequest
    >({
      query: (body) => ({
        url: 'oapi/v1/billing/checkout',
        method: 'POST',
        body,
      }),
    }),

    completeCheckout: builder.mutation<
      CheckoutSessionStatusResponse,
      { sessionId: string }
    >({
      query: (body) => ({
        url: 'oapi/v1/billing/complete',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscription', 'SubscriptionInvoice'],
    }),

    createSetupIntent: builder.mutation<
      CreateSetupIntentResponse,
      CreateSetupIntentRequest
    >({
      query: (body) => ({
        url: 'oapi/v1/billing/setup-intent',
        method: 'POST',
        body,
      }),
    }),

    updatePaymentMethod: builder.mutation<
      UpdatePaymentMethodResponse,
      UpdatePaymentMethodRequest
    >({
      query: (body) => ({
        url: 'oapi/v1/billing/payment-method',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Subscription', 'SubscriptionInvoice'],
    }),

    updateSubscriptionQuantity: builder.mutation<
      SubscriptionLookup,
      { userId: string; unit: number }
    >({
      query: ({ userId, unit }) => ({
        url: `oapi/v1/subscriptions?userId=${encodeURIComponent(userId)}`,
        method: 'PATCH',
        body: { unit },
      }),
      invalidatesTags: ['Subscription', 'SubscriptionInvoice'],
    }),

    cancelSubscription: builder.mutation<SubscriptionLookup, { userId: string }>({
      query: ({ userId }) => ({
        url: `oapi/v1/subscriptions/cancel?userId=${encodeURIComponent(userId)}`,
        method: 'POST',
      }),
      invalidatesTags: ['Subscription', 'SubscriptionInvoice'],
    }),

    reactivateSubscription: builder.mutation<
      SubscriptionLookup,
      { userId: string; unit: number }
    >({
      query: ({ userId, unit }) => ({
        url: `oapi/v1/subscriptions/reactivate?userId=${encodeURIComponent(userId)}`,
        method: 'POST',
        body: { unit },
      }),
      invalidatesTags: ['Subscription', 'SubscriptionInvoice'],
    }),

    getSubscriptionInvoices: builder.query<
      SubscriptionInvoice[],
      { userId: string; year: number }
    >({
      query: ({ userId, year }) => ({
        url: `oapi/v1/subscriptions/invoices?userId=${encodeURIComponent(
          userId
        )}&year=${year}`,
      }),
      providesTags: ['SubscriptionInvoice'],
    }),
  }),
})

export const {
  useGetSubscriptionByUserIdQuery,
  useLazyGetSubscriptionByUserIdQuery,
  useCreateCheckoutSessionMutation,
  useCompleteCheckoutMutation,
  useCreateSetupIntentMutation,
  useUpdatePaymentMethodMutation,
  useUpdateSubscriptionQuantityMutation,
  useCancelSubscriptionMutation,
  useReactivateSubscriptionMutation,
  useGetSubscriptionInvoicesQuery,
} = subscriptionsApi
